import React, { useRef, useEffect, useState } from "react";

import ChatInput from "./ChatInput_Component";
// Import typed Redux hooks
import { useAppDispatch, useAppSelector } from "./hooks/reduxHooks";
import ConversationStarters from "./ConversationStarters_Component";
import UploadTable from "./UploadTable_Component";
import GenerationProgressBar from "./GenerationProgressBar_Component";
import SuggestedActionButton from "./SuggestedActionButton_Component";
import { HandThumbUpIcon, HandThumbDownIcon, ArrowUpTrayIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  HandThumbUpIcon as HandThumbUpIconSolid,
  HandThumbDownIcon as HandThumbDownIconSolid,
  CheckCircleIcon,
} from "@heroicons/react/24/solid";
import { sendMessage, addMessage, selectVisibleMessages, selectTurnTodoPlan, setUseCaseSelected, setSelectedUseCase, selectChatHistoryLoading, selectChatHistoryError, setChatHistoryError, updateUploadInTable } from "./ChatSlice_Store";
import { selectCurrentProjectId } from "./ProjectSlice_Store";
import { uploadFileToProject } from "./services/fileUploadService";
import {
  ALLOWED_EXTENSIONS_DISPLAY,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  isFileTypeAllowed,
  formatFileSize,
} from './utils/fileUploadUtils';
import InlineFactBadge from "./components/InlineFactBadge";
import { selectAllFacts } from "./ResultsSlice_Store";
import TableSkeleton from "./components/TableSkeleton";
import MarkdownTable from "./components/MarkdownTable";
// Phase 7: Switch to new streaming system
import { useStreamingMessage } from "./hooks/useStreamingMessage";
import { setLayoutMode } from "./LayoutSlice_Store";
import { openDocumentPreview } from "./DocumentPreviewSlice_Store";
import { buildDocumentPreviewPayload, buildHighlightRequest } from "./services/factPreviewService";
import { canPreviewDocument } from "./services/documentPreviewService";
import { useIsTablet } from "./hooks/useIsTablet";
import { useVirtualKeyboard } from "./hooks/useVirtualKeyboard";
import ChatMarkdownRenderer from "./components/ChatMarkdownRenderer";
import InlineResultsTabs from "./InlineResultsTabs_Component";

/**
 * Feature-specific limitations text shown in the BETA info bubble
 */
const USE_CASE_LIMITATIONS: Record<string, string> = {
  'past-deals': 'DealOS only ingests published PE deals that reached PIR after 2011. Sensitive deals, flash / IPV reports, PCRs not currently indexed. Other strategies coming soon.',
  'offer-letter': 'Offer letter drafting works best with a process letter and example offer letters as inputs. Output quality depends on the detail and relevance of uploaded documents.',
  'intro-note': 'Intro note drafting works best with CIMs, financial summaries, and deal memos as inputs. Output quality depends on the detail and relevance of uploaded documents.',
  'research-desk': 'Research Desk surfaces IC papers, sector analyses, market overviews, and company profiles from the CVC research library. Coverage depends on what has been indexed — newly published research may not appear immediately.',
};

/**
 * Document color palette for fact badges
 * Uses CVC secondary colors for visual distinction across documents
 */
const DOCUMENT_COLORS = [
  { bg: 'bg-cvc-lb2', text: 'text-cvc-blue', border: 'border-cvc-lb1' },
  { bg: 'bg-emerald-50', text: 'text-cvc-green', border: 'border-emerald-200' },
  { bg: 'bg-cyan-50', text: 'text-cvc-marine', border: 'border-cyan-200' },
  { bg: 'bg-purple-50', text: 'text-cvc-purple', border: 'border-purple-200' },
  { bg: 'bg-red-50', text: 'text-cvc-coral', border: 'border-red-200' },
  { bg: 'bg-amber-50', text: 'text-cvc-yellow', border: 'border-amber-200' },
];

/**
 * Get color scheme for a document based on its display index
 */
function getDocumentColor(documentDisplayIndex: number): { bg: string; text: string; border: string } {
  const index = (documentDisplayIndex - 1) % DOCUMENT_COLORS.length;
  return DOCUMENT_COLORS[index] || DOCUMENT_COLORS[0];
}

/**
 * Check if a message is an internal system message that should be hidden from the UI
 * These are control messages used for internal communication between frontend and backend
 */
function isInternalMessage(message: { text?: string; sender?: string }): boolean {
  const text = message.text || '';
  // Hide CONTROL_* messages (file uploads, feedback, etc.)
  if (text.startsWith('CONTROL_')) return true;
  // Hide SYSTEM_* messages (status checks, triggers, etc.)
  if (text.startsWith('SYSTEM_')) return true;
  return false;
}


interface ChatFactFooterProps {
  message: { text?: string; turnId?: string } | null;
  onDocumentClick: (document: any) => void;
  allFacts: Array<{ factId: string; documentId?: string }>;
  allDocuments: Array<{ documentId: string; title?: string; documentName?: string; originalFilename?: string }>;
}

/**
 * Phase 7: Wrapper component for bot messages that uses the new streaming hook
 * This component calls useStreamingMessage for a single message
 */
interface BotMessageProps {
  message: any;
  allFacts: any[];
  allDocuments: any[];
  renderBotMessageWithFacts: (message: any, allFacts: any[], allDocuments: any[], text: string, isStreaming: boolean) => (string | JSX.Element)[] | JSX.Element;
}

function BotMessageContent({ message, allFacts, allDocuments, renderBotMessageWithFacts }: BotMessageProps): JSX.Element {
  // Phase 8: Use the new streaming hook to get displayedText
  // Only use streaming system for messages with a turnId
  const streamingData = useStreamingMessage(message.turnId || '');

  // Track when streaming just completed (for fade-in animation)
  const [justCompleted, setJustCompleted] = React.useState(false);
  const prevIsComplete = React.useRef(streamingData.isComplete);

  React.useEffect(() => {
    // Detect transition from not-complete to complete (stream finished)
    if (streamingData.isComplete && !prevIsComplete.current) {
      setJustCompleted(true);
      // Clear the flag after animation completes (match CSS duration)
      const timer = setTimeout(() => setJustCompleted(false), 600);
      return () => clearTimeout(timer);
    }
    prevIsComplete.current = streamingData.isComplete;
  }, [streamingData.isComplete]);

  // If message has no turnId, just use message.text directly (welcome messages, etc.)
  if (!message.turnId) {
    const text = message.text || '';
    return (
      <>
        {renderBotMessageWithFacts(message, allFacts, allDocuments, text, false)}
      </>
    );
  }

  // For messages with turnId, determine which text to use and whether we're streaming
  const text = streamingData.displayedText || message.text || '';

  // We're actively streaming if we have displayedText and NOT (complete AND finished animating)
  const isStreaming = !!streamingData.displayedText && !(streamingData.isComplete && !streamingData.isAnimating);

  return (
    <span className={justCompleted ? 'streaming-fade-in' : ''}>
      {renderBotMessageWithFacts(message, allFacts, allDocuments, text, isStreaming)}
      <style>{`
        .streaming-fade-in {
          animation: streamingFadeIn 0.6s ease-out;
        }
        @keyframes streamingFadeIn {
          from {
            opacity: 0.6;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </span>
  );
}

/**
 * Chat fact footer - shows color-coded document list for a message
 * Phase 8: Reads text from streaming state when available, falls back to message.text
 */
function ChatFactFooter({ message, onDocumentClick, allFacts, allDocuments }: ChatFactFooterProps): JSX.Element | null {
  // Phase 8: Use streaming hook to get the current text (whether streaming or finalized)
  const streamingData = useStreamingMessage(message?.turnId || '');

  if (!message || !allFacts || !allDocuments) {
    return null;
  }

    // Phase 8: Get text from streaming state if available, otherwise use message.text
    // During streaming: streamingData.displayedText has the animated text
    // After finalization: message.text has the final persisted text
    const messageText = streamingData.displayedText || message.text || "";
    const factIdsInMessage = new Set<string>();
    const placeholderPattern = /\[\[FACT:([^\]]+)\]\]/g;
    let match: RegExpExecArray | null;
    while ((match = placeholderPattern.exec(messageText)) !== null) {
      const factId = match[1];
      factIdsInMessage.add(factId);
    }

  if (factIdsInMessage.size === 0) {
    return null;
  }

  // Collect unique document IDs from facts referenced in this message
  const docIds = Array.from(
    new Set(
      allFacts
        .filter(f => factIdsInMessage.has(f.factId))
        .map(f => f.documentId)
        .filter(Boolean)
    )
  );

  if (docIds.length === 0) {
    return null;
  }

  // Resolve full document objects from central documents array
  const turnDocs = allDocuments.filter(doc =>
    docIds.includes(doc.documentId)
  );

  if (turnDocs.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 pt-2 border-t border-cvc-grey">
      <div className="text-xs text-cvc-text/70 mb-1.5 font-cvcBody">Sources referenced</div>
      <div className="flex flex-col gap-1.5">
        {turnDocs.map((document, index) => {
          const docIndex = index + 1;
          const colors = getDocumentColor(docIndex);

          // Backend normalizes originalFilename to title field
          const title = document.title || document.documentName || `Document ${docIndex}`;

          return (
            <button
              key={document.documentId || index}
              onClick={() => onDocumentClick(document)}
              title={title}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs ${colors.bg} border ${colors.border} hover:opacity-80 transition-opacity w-full text-left group`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`h-4 w-4 flex-shrink-0 ${colors.text}`}>
                <path fillRule="evenodd" d="M4.5 2A1.5 1.5 0 003 3.5v13A1.5 1.5 0 004.5 18h11a1.5 1.5 0 001.5-1.5V7.621a1.5 1.5 0 00-.44-1.06l-4.12-4.122A1.5 1.5 0 0011.378 2H4.5zm2.25 8.5a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5zm0 3a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5z" clipRule="evenodd" />
              </svg>
              <span className={`truncate font-medium ${colors.text}`}>{title}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface ChatbotLayoutProps {
  chatInputAriaLabel?: string;
  inWorkspaceMode?: boolean;
}

export default function ChatbotLayout_Component({ chatInputAriaLabel = "Message input field", inWorkspaceMode = false }: ChatbotLayoutProps): JSX.Element {
  // Phase 7: Animation now handled by RTK Listener middleware
  // The new system reads displayedText per-message using useStreamingMessage(messageId)

  // Add custom CSS for animation delays and streaming text effect
  const sparkleStyles = `
    .animation-delay-200 { animation-delay: 0.2s; }
    .animation-delay-400 { animation-delay: 0.4s; }
    @keyframes fadeInType {
      0% {
        opacity: 0;
      }
      100% {
        opacity: 1;
      }
    }

    /* Streaming text fade-in animation */
    @keyframes streamingFadeIn {
      0% {
        opacity: 0;
        filter: blur(1px);
      }
      100% {
        opacity: 1;
        filter: blur(0);
      }
    }

    .streaming-text {
      animation: streamingFadeIn 0.2s ease-in;
    }
  `;

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = sparkleStyles;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  const dispatch = useAppDispatch();

  // Drag-and-drop state
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [dropError, setDropError] = useState<string>("");

  // Get upload-related state from Redux
  const currentProjectId = useAppSelector(selectCurrentProjectId);
  const reduxAuth = useAppSelector(state => state.userAuthAndAdminSlice);
  const useCaseSelected = useAppSelector(state => state.chatSlice.useCaseSelected);
  const selectedUseCase = useAppSelector(state => state.chatSlice.selectedUseCase);

  // Limitations info bubble state
  const [limitationsDismissed, setLimitationsDismissed] = useState(false);
  const prevSelectedUseCaseRef = useRef<string | null>(null);

  // Reset limitations bubble when use case changes
  useEffect(() => {
    if (selectedUseCase !== prevSelectedUseCaseRef.current) {
      setLimitationsDismissed(false);
      prevSelectedUseCaseRef.current = selectedUseCase;
    }
  }, [selectedUseCase]);
  const projectReady = Boolean(currentProjectId);
  const isUploadDisabledForUseCase = selectedUseCase === 'past-deals' || selectedUseCase === 'research-desk';
  const isDropEnabled = projectReady && useCaseSelected && !isUploadDisabledForUseCase;

  // Helper to get current user ID (same pattern as ChatInput)
  const getCurrentUserId = (): string => {
    if (reduxAuth?.isAuthenticated && reduxAuth?.userId) {
      return reduxAuth.userId;
    }
    if (reduxAuth?.isAuthenticated && reduxAuth?.userEmail) {
      return reduxAuth.userEmail;
    }
    return "test.user";
  };

  // Upload a single dropped file
  const uploadDroppedFile = async (file: File): Promise<void> => {
    const uploadId = `upload-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    dispatch(updateUploadInTable({
      uploadId,
      fileName: file.name,
      fileSize: file.size,
      progress: 0,
      status: "Preparing upload...",
      completed: false,
      error: false,
    }));

    try {
      const { filePath } = await uploadFileToProject({
        file,
        username: getCurrentUserId(),
        projectId: currentProjectId,
        onStatusChange: (status: string) => {
          if (status === "fetching_sas") {
            dispatch(updateUploadInTable({
              uploadId,
              status: "Getting upload URL...",
            }));
          }
          if (status === "uploading") {
            dispatch(updateUploadInTable({
              uploadId,
              status: "Uploading file...",
            }));
          }
        },
        onProgress: ({ percent }: { percent: number | null }) => {
          if (percent === null || percent === undefined) return;
          dispatch(updateUploadInTable({
            uploadId,
            progress: percent,
          }));
        },
      });

      if (currentProjectId && filePath) {
        dispatch(updateUploadInTable({
          uploadId,
          progress: 100,
          status: "Uploaded. Waiting for processing...",
          completed: false,
        }));

        const controlMessage = `CONTROL_FILE_UPLOAD:${currentProjectId}:${filePath}`;
        dispatch(sendMessage(controlMessage));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      dispatch(updateUploadInTable({
        uploadId,
        status: `Upload failed: ${errorMessage}`,
        error: true,
      }));
    }
  };

  // Drag-and-drop event handlers
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();

    // Only show overlay if uploading is enabled and files are being dragged
    if (!isDropEnabled) return;
    if (!e.dataTransfer.types.includes('Files')) return;

    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();

    // Check if we're actually leaving the container (not just moving to a child)
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDraggingOver(false);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    // Silent ignore if upload is disabled
    if (!isDropEnabled) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length === 0) return;

    // Validate file types - reject ALL if any invalid
    const invalidTypeFiles = droppedFiles.filter(file => !isFileTypeAllowed(file.name));
    if (invalidTypeFiles.length > 0) {
      const invalidNames = invalidTypeFiles.map(f => f.name).join(', ');
      setDropError(`Unsupported file type: ${invalidNames}. Allowed formats: ${ALLOWED_EXTENSIONS_DISPLAY}`);
      return;
    }

    // Validate file sizes - reject ALL if any oversized
    const oversizedFiles = droppedFiles.filter(file => file.size > MAX_FILE_SIZE_BYTES);
    if (oversizedFiles.length > 0) {
      if (oversizedFiles.length === 1) {
        const file = oversizedFiles[0];
        setDropError(`"${file.name}" (${formatFileSize(file.size)}) exceeds the ${MAX_FILE_SIZE_MB} MB limit.`);
      } else {
        const fileList = oversizedFiles.map(f => `${f.name} (${formatFileSize(f.size)})`).join(', ');
        setDropError(`${oversizedFiles.length} files exceed the ${MAX_FILE_SIZE_MB} MB limit: ${fileList}`);
      }
      return;
    }

    // Clear any previous error and upload all files in parallel
    setDropError("");
    const uploadResults = await Promise.allSettled(droppedFiles.map(file => uploadDroppedFile(file)));

    // Surface which files failed without interrupting other uploads
    const failedFiles = uploadResults
      .map((result, index) => (result.status === "rejected" ? droppedFiles[index].name : null))
      .filter((name): name is string => name !== null);

    if (failedFiles.length > 0) {
      setDropError(`Failed to upload: ${failedFiles.join(", ")}`);
    }
  };

  // Auto-dismiss drop error after 5 seconds
  useEffect(() => {
    if (dropError) {
      const timer = setTimeout(() => setDropError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [dropError]);

  // Helper function to render bot message text with inline facts and file links
  // Phase 8: Simplified to use explicit boolean flag for streaming state
  const renderBotMessageWithFacts = (message: any, allFacts: any[], allDocuments: any[], text: string, isStreaming: boolean): (string | JSX.Element)[] | JSX.Element => {

    // Build a fact index map for this message (per-message numbering)
    // For newly streamed assistant text, hallucinated fact markers are filtered server-side,
    // so markers in the streaming text chunks are expected to be valid.
    const factIdToIndex: Record<string, number> = {};
    let nextIndex = 1;

    // Parse fact placeholders from text to build the index map
    // Fact format from new parser: [[FACT:factId]]
    const factPattern = /\[\[FACT:([^\]]+)\]\]/g;
    let match: RegExpExecArray | null;
    while ((match = factPattern.exec(text)) !== null) {
      const factId = match[1];
      if (!factIdToIndex[factId]) {
        factIdToIndex[factId] = nextIndex++;
      }
    }

    // Build document ID to display index map for THIS MESSAGE ONLY
    // This ensures badge colors match the source footer (per-message document ordering)
    const factIdsInMessage = new Set(Object.keys(factIdToIndex));
    const documentIdsInMessage = new Set<string>();

    // Collect unique document IDs referenced by facts in this message
    for (const factId of factIdsInMessage) {
      const fact = allFacts.find((f: any) => f.factId === factId);
      if (fact?.documentId) {
        documentIdsInMessage.add(fact.documentId);
      }
    }

    // Create ordered array of document IDs (preserving order from allDocuments for consistency)
    const orderedDocumentIds = allDocuments
      .filter((d: any) => documentIdsInMessage.has(d.documentId))
      .map((d: any) => d.documentId);

    // Build document ID -> display index map (1-based)
    const documentIdToIndex: Record<string, number> = {};
    orderedDocumentIds.forEach((docId, idx) => {
      documentIdToIndex[docId] = idx + 1;
    });

    // Helper to get fact display index
    const getFactDisplayIndex = (factId: string): number | undefined => {
      return factIdToIndex[factId];
    };

    // Helper to get document display index for a fact
    // Uses per-message document ordering so badge colors match the footer
    const getDocumentDisplayIndex = (factId: string): number | null => {
      // Look up fact by factId from central store
      const fact = allFacts.find((f: any) => f.factId === factId);

      if (!fact || !fact.documentId) {
        return null;
      }

      // Return per-message document index (1-based)
      return documentIdToIndex[fact.documentId] || 1;
    };

    // Create a callback to render fact badges - this is passed to ChatMarkdownRenderer
    // so facts can be rendered inline within the markdown structure
    // Hallucinated markers are filtered server-side, so all markers here are valid
    const renderFactBadge = (factId: string, key: string): React.ReactNode => {
      const fact = allFacts.find((f: any) => f.factId === factId);
      const displayIndex = getFactDisplayIndex(factId);
      const documentDisplayIndex = getDocumentDisplayIndex(factId);

      return (
        <InlineFactBadge
          key={key}
          factId={factId}
          fact={fact}
          displayIndex={displayIndex}
          documentDisplayIndex={documentDisplayIndex}
          allFacts={allFacts}
          factIdToIndex={factIdToIndex}
          loading={false}
        />
      );
    };

    // Check if text contains tables (which need special block-level handling)
    const hasTable = text.includes('[[TABLE:') || text.includes('[[TABLE_LOADING:');

    // Simple case: no tables - render entire text through markdown with fact callback
    if (!hasTable) {
      return (
        <ChatMarkdownRenderer
          content={text}
          renderFactBadge={renderFactBadge}
        />
      );
    }

    // Complex case: has tables - segment only for tables, render text segments with markdown
    // Pattern to match table placeholders
    const tablePattern = /\[\[(TABLE|TABLE_LOADING):([^\]:]+)(?::([^\]]+))?\]\]/g;
    const segments: Array<{ type: 'text' | 'table' | 'table_loading'; content: string; blockId?: string }> = [];
    let lastIndex = 0;
    let tableMatch: RegExpExecArray | null;

    while ((tableMatch = tablePattern.exec(text)) !== null) {
      // Add text before this table
      if (tableMatch.index > lastIndex) {
        segments.push({ type: 'text', content: text.substring(lastIndex, tableMatch.index) });
      }

      // Add the table
      const [, tableType, blockId, encodedContent] = tableMatch;
      if (tableType === 'TABLE') {
        const decodedContent = encodedContent ? decodeURIComponent(encodedContent) : '';
        segments.push({ type: 'table', content: decodedContent, blockId });
      } else {
        segments.push({ type: 'table_loading', content: '', blockId });
      }

      lastIndex = tablePattern.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      segments.push({ type: 'text', content: text.substring(lastIndex) });
    }

    // Render segments
    return segments.map((segment, index) => {
      if (segment.type === 'table') {
        return <MarkdownTable key={`table-${segment.blockId}`} content={segment.content} fadeIn={true} />;
      } else if (segment.type === 'table_loading') {
        return <TableSkeleton key={`table-loading-${segment.blockId}`} />;
      } else {
        // Text segment - render through markdown with fact callback
        return (
          <ChatMarkdownRenderer
            key={`text-${index}`}
            content={segment.content}
            renderFactBadge={renderFactBadge}
          />
        );
      }
    });
  };

  const messages = useAppSelector(selectVisibleMessages);
  const isBotTyping = useAppSelector((state) => state.chatSlice.isBotTyping);
  const currentWorkflowStatus = useAppSelector((state) => state.chatSlice.currentWorkflowStatus);
  const chatInputReady = useAppSelector((state) => state.chatSlice.chatInputReady);
  const chatHistoryLoading = useAppSelector(selectChatHistoryLoading);
  const chatHistoryError = useAppSelector(selectChatHistoryError);

  // State for loading overlay fade animation - initialize based on current loading state
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(chatHistoryLoading);
  const [loadingOverlayVisible, setLoadingOverlayVisible] = useState(false);

  // Handle loading overlay fade in/out
  useEffect(() => {
    if (chatHistoryLoading) {
      // Fade in: show overlay immediately, then fade in after brief delay
      setShowLoadingOverlay(true);
      const fadeInTimer = setTimeout(() => {
        setLoadingOverlayVisible(true);
      }, 50); // Small delay to ensure DOM is ready for transition
      return () => clearTimeout(fadeInTimer);
    } else if (showLoadingOverlay) {
      // Fade out: hide visibility first, then remove from DOM
      setLoadingOverlayVisible(false);
      const timer = setTimeout(() => {
        setShowLoadingOverlay(false);
      }, 300); // Match CSS transition duration
      return () => clearTimeout(timer);
    }
  }, [chatHistoryLoading, showLoadingOverlay]);

  // Use selectors for todo plan data
  // Derive plan from the workflow status's turnId (not selectCurrentTurnId) to avoid
  // showing a stale plan from a rehydrated previous turn when a new workflow starts.
  const currentTurnId = currentWorkflowStatus?.turnId ?? null;
  const currentTurnTodoPlan = useAppSelector((state) =>
    currentTurnId ? selectTurnTodoPlan(state, currentTurnId) : null
  );

  // Debug logging for turn plan
  React.useEffect(() => {
    console.log('🎯 ChatbotLayout turn plan data:', {
      currentTurnId,
      currentTurnTodoPlan,
      hasPlan: !!currentTurnTodoPlan,
      hasSteps: (currentTurnTodoPlan as { steps?: any[] })?.steps?.length || 0
    });
  }, [currentTurnId, currentTurnTodoPlan]);

  // Auto-scroll when research plan is shown or updated
  React.useEffect(() => {
    if (currentTurnTodoPlan && (currentTurnTodoPlan as { steps?: any[] })?.steps?.length > 0) {
      // Small delay to ensure the research plan has rendered
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [currentTurnTodoPlan]);

  // Phase 3: Read facts and documents from central ResultsSlice
  const allFacts = useAppSelector(selectAllFacts);
  const allDocuments = useAppSelector((state) => state.resultsSlice?.documentRegistry || []);

  // Handler for document click in footer - opens PDF viewer with all facts for that document
  const handleDocumentClick = (document: any): void => {
    if (!document || !document.documentId) {
      console.warn("Cannot open document: missing document or documentId");
      return;
    }

    // Build document preview payload
    const documentToUse = buildDocumentPreviewPayload(document, null);

    // Check if document can be previewed
    if (!canPreviewDocument(documentToUse)) {
      console.warn("Document cannot be previewed:", documentToUse);
      return;
    }

    // Gather ALL facts for this document from the central store
    const documentFacts = allFacts.filter((f: any) => f.documentId === document.documentId);

    if (documentFacts.length === 0) {
      console.warn("No facts found for document:", document.documentId);
      // Still open the document even if no facts are found
    }

    // Build highlight request from ALL document facts
    const factCompatibleObject = {
      documentId: document.documentId,
      facts: documentFacts
    };

    // Build highlight request (no specific fact is preferred when clicking source button)
    const highlightRequest = buildHighlightRequest(factCompatibleObject, null);

    // Attach all document facts to the highlight request
    if (highlightRequest) {
      highlightRequest.allDocumentFacts = documentFacts;
    }

    // Switch to workspace mode so the preview panel is visible
    dispatch(setLayoutMode('workspace'));

    // Open the document preview with all document facts
    dispatch(openDocumentPreview({ document: documentToUse, highlightRequest }));
  };

  // Phase 8: Select streaming messages for auto-scroll dependency
  // When displayedText changes during animation, we need to trigger auto-scroll
  const streamingMessages = useAppSelector((state) => (state as any).streaming?.messages || {});

  const chatMessages = Array.isArray(messages) ? messages : [];

  // State for conversation starter input
  const [inputValue, setInputValue] = useState<string>("");

  // Note: Fact/table loading state now driven by [[FACT_LOADING:*]] and [[TABLE_LOADING:*]] placeholders
  // No more timer-based loading state tracking needed

  // Check if user has sent any messages (exclude bot messages and welcome message)
  const userMessageCount = chatMessages.filter(msg => msg.sender === "user").length;
  // Show pills only if no user messages and not loading history
  // Hide conversation starters when history is loading or when messages exist
  const showConversationStarters = userMessageCount === 0 && !chatHistoryLoading && chatMessages.length <= 1;
  const isTablet = useIsTablet();
  const { isKeyboardOpen } = useVirtualKeyboard({ enabled: isTablet });
  // Pills are disabled after one bot message beyond the welcome
  const pillsDisabled = chatMessages.length > 1;

  // Handle conversation starter selection
  const handleSelectStarter = (followUpPrompt: string, useCaseId: string): void => {
    if (!followUpPrompt) {
      return;
    }

    // Set the specific use case ID (e.g., 'past-deals', 'offer-letter')
    dispatch(setSelectedUseCase(useCaseId));

    // Optional: log the selection for debugging
    console.log('💡 Conversation starter selected:', useCaseId, followUpPrompt);

    // Add the starter prompt as a bot message (not sent to core-agent)
    dispatch(addMessage({ sender: "chatbot", text: followUpPrompt }));
  };

  // Track feedback state for each message: { messageIndex: 'thumbs_up' | 'thumbs_down' | null }
  const [messageFeedback, setMessageFeedback] = useState<Record<number, 'thumbs_up' | 'thumbs_down'>>({});
  // Track scaling animation state
  const [scalingMessages, setScalingMessages] = useState<Set<number>>(new Set());
  // Track text feedback for thumbs down
  const [textFeedback, setTextFeedback] = useState<Record<number, string>>({});
  const [showTextFeedback, setShowTextFeedback] = useState<Record<number, boolean>>({});
  // Track the latest bot message index to disable older feedback controls
  const [latestBotMessageIndex, setLatestBotMessageIndex] = useState<number>(-1);

  const handleFeedback = (messageIndex: number, feedbackType: 'thumbs_up' | 'thumbs_down'): void => {
    const message = chatMessages[messageIndex];
    if (message && message.sender === "chatbot" && message.turnId) {
      // Update the feedback state for this message
      setMessageFeedback(prev => ({
        ...prev,
        [messageIndex]: feedbackType
      }));

      // Trigger scaling animation
      setScalingMessages(prev => new Set(prev).add(messageIndex));

      // Remove scaling animation after 0.3 seconds
      setTimeout(() => {
        setScalingMessages(prev => {
          const newSet = new Set(prev);
          newSet.delete(messageIndex);
          return newSet;
        });
      }, 300);

      // Send immediate feedback for both thumbs up and thumbs down
      const controlMessage = `CONTROL_FEEDBACK:${feedbackType}:${message.turnId}:${message.text.substring(0, 50)}...`;
      dispatch(sendMessage(controlMessage));

      // Show text feedback box for thumbs down (for optional additional comment)
      if (feedbackType === 'thumbs_down') {
        setShowTextFeedback(prev => ({
          ...prev,
          [messageIndex]: true
        }));
        // Auto-scroll to bottom to show feedback section
        setTimeout(() => {
          scrollToBottom();
        }, 100); // Small delay to ensure the feedback box renders first
      } else {
        // Hide text feedback for thumbs up
        setShowTextFeedback(prev => ({
          ...prev,
          [messageIndex]: false
        }));
      }
    }
  };

  const handleTextFeedbackSubmit = (messageIndex: number): void => {
    const message = chatMessages[messageIndex];
    const textComment = textFeedback[messageIndex] || "";

    if (message && message.sender === "chatbot" && message.turnId) {
      // Send control message with text feedback using turnId
      const controlMessage = `CONTROL_FEEDBACK:thumbs_down:${message.turnId}:${message.text.substring(0, 50)}...:TEXT:${textComment}`;
      dispatch(sendMessage(controlMessage));

      // Clear the text feedback and hide the box
      setTextFeedback(prev => ({
        ...prev,
        [messageIndex]: ""
      }));
      setShowTextFeedback(prev => ({
        ...prev,
        [messageIndex]: false
      }));
    }
  };

  const bottomRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Scroll the messages container to the bottom without triggering iOS viewport scroll.
  // scrollIntoView() on iOS WebKit can scroll all ancestors including the viewport,
  // so we use scrollTop directly on the container instead.
  const scrollToBottom = (smooth = true): void => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (smooth) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    } else {
      container.scrollTop = container.scrollHeight;
    }
  };

  // Phase 8: Auto-scroll with streaming support
  // Use requestAnimationFrame to batch scroll updates during streaming animation
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      scrollToBottom();
    });
    return () => cancelAnimationFrame(rafId);
  }, [chatMessages, currentTurnTodoPlan, streamingMessages]); // Re-run whenever messages, research plan, or streaming text changes

  // Track when new messages are added and update latest bot message index
  useEffect(() => {
    // Find the latest bot message index
    let latestBotIndex = -1;
    for (let i = chatMessages.length - 1; i >= 0; i--) {
      if (chatMessages[i] && chatMessages[i].sender === "chatbot") {
        latestBotIndex = i;
        break;
      }
    }

    // If we have a new latest bot message, clear text feedback boxes for older messages
    if (latestBotIndex !== latestBotMessageIndex && latestBotIndex > latestBotMessageIndex) {
      // Clear all text feedback boxes
      setShowTextFeedback({});
      setTextFeedback({});
      setLatestBotMessageIndex(latestBotIndex);
    }
  }, [chatMessages, latestBotMessageIndex]);

  // Note: All timer-based loading logic removed - now driven by [[FACT_LOADING:*]] placeholders

  return (
    <div
      className={`w-full h-full flex flex-col font-cvcBody relative ${showLoadingOverlay ? 'bg-transparent' : 'bg-white'}`}
      onDragOver={handleDragOver}
      onDragEnter={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag-and-drop overlay */}
      {isDraggingOver && (
        <div className="absolute inset-0 z-50 bg-cvc-blue/10 border-2 border-dashed border-cvc-blue rounded-lg flex items-center justify-center pointer-events-none">
          <div className="bg-white rounded-lg shadow-lg px-8 py-6 flex flex-col items-center">
            <ArrowUpTrayIcon className="w-12 h-12 text-cvc-blue mb-3" />
            <span className="text-lg font-medium text-cvc-blue">Drop files to upload</span>
            <span className="text-sm text-cvc-text/70 mt-1">Supported: {ALLOWED_EXTENSIONS_DISPLAY}</span>
          </div>
        </div>
      )}

      {/* Drop error toast */}
      {dropError && (
        <div className="absolute top-4 left-4 right-4 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 shadow-lg flex items-start gap-2">
            <div className="flex-1">
              <p className="text-sm text-red-800 font-medium">File upload error</p>
              <p className="text-xs text-red-600 mt-1">{dropError}</p>
            </div>
            <button
              type="button"
              onClick={() => setDropError("")}
              className="text-red-400 hover:text-red-600 p-1"
              aria-label="Dismiss error"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Loading modal - full screen centered when loading existing conversation */}
      {showLoadingOverlay ? (
        <div
          className={`flex-1 flex items-center justify-center transition-opacity duration-300 ${
            loadingOverlayVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="bg-white rounded-lg shadow-lg px-8 py-6 flex flex-col items-center">
            <svg className="w-8 h-8 text-cvc-blue animate-spin mb-3" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm text-cvc-text font-normal">Loading conversation history...</span>
          </div>
        </div>
      ) : (
      <>
      {/* Message list - responsive padding and sizing */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
        {/* Chat history error banner */}
        {chatHistoryError && !chatHistoryLoading && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-red-800">Failed to load conversation history</h3>
                  <p className="mt-1 text-sm text-red-700">{chatHistoryError}</p>
                </div>
              </div>
              <button
                onClick={() => dispatch(setChatHistoryError(null))}
                className="flex-shrink-0 ml-3 text-red-600 hover:text-red-800"
                aria-label="Dismiss error"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {!chatHistoryLoading && chatMessages.map((message, index) => {
          // Skip internal system messages (CONTROL_*, SYSTEM_*)
          if (isInternalMessage(message)) {
            return null;
          }

          // Render reconnection status messages as inline status bars
          if (message.type?.startsWith("reconnection-")) {
            const isError = message.type === "reconnection-error";
            const isSuccess = message.type === "reconnection-success";
            const colorClass = isError
              ? "text-red-600 bg-red-50 border-red-200"
              : isSuccess
              ? "text-green-700 bg-green-50 border-green-200"
              : "text-gray-500 bg-gray-50 border-gray-200";
            return (
              <div key={index} className="flex justify-center my-2">
                <div className={`text-xs px-3 py-1.5 rounded-full border ${colorClass}`}>
                  {message.text}
                </div>
              </div>
            );
          }

          const isUser = message.sender === "user";
          // Special styling for the first bot message (welcome message)
          const isWelcomeMessage = index === 0 && !isUser;

          return (
            <React.Fragment key={index}>
              <div
                className={`flex ${isUser ? "justify-end" : "justify-start"}`}
              >
                {/* User messages: bubble on right side */}
                {isUser ? (
                  <div className="flex w-full max-w-[85%] sm:max-w-xs lg:max-w-md justify-end">
                    <div className="px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-cvc-grey text-cvc-text">
                      <p className="text-sm sm:text-base leading-relaxed whitespace-pre-line break-words">
                        {message.text}
                      </p>
                    </div>
                  </div>
                ) : isWelcomeMessage && showConversationStarters && !inWorkspaceMode ? (
                  /* Welcome message: centered header styling - only show while starters are visible, never in workspace mode */
                  <div className="w-full text-center py-3 sm:py-8">
                    <h1
                      className="text-lg leading-6 sm:text-[40px] sm:leading-[40px] tablet:text-[30px] tablet:leading-[32px] font-medium mb-4 font-cvcDisplay"
                      style={{ color: '#234E81' }}
                    >
                      Welcome to DealOS,<br />CVC's proprietary AI platform.
                    </h1>
                    <p className="text-base leading-6 text-cvc-text max-w-2xl mx-auto px-4 font-normal font-cvcBody">
                      Try our beta tasks below, with more powerful features on the way.
                    </p>
                  </div>
                ) : isWelcomeMessage && (selectedUseCase === 'past-deals' || selectedUseCase === 'research-desk') ? (
                  /* Hide welcome message once conversation starters are gone (search use cases only) */
                  null
                ) : (
                  /* Bot messages: full-width without bubble */
                  <div className="w-full">
                    {message.type === "generation-progress" ? (
                      <GenerationProgressBar
                        projectId={message.projectId as string}
                        startTime={message.startTime as number}
                        status={message.status as 'generating' | 'processed' | 'failed' | 'timeout'}
                        error={message.error as string | undefined}
                      />
                    ) : message.type === "upload-table" ? (
                      <UploadTable uploads={Object.values(message.uploads || {})} />
                    ) : message.type === "upload-ready-prompt" ? (
                      <div>
                        <p className="text-sm sm:text-base leading-relaxed whitespace-pre-line break-words text-gray-700">
                          {message.text}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <SuggestedActionButton
                            action={{
                              type: 'send_message',
                              label: 'Start Drafting',
                              icon: 'play',
                              message: 'SYSTEM_START_DRAFTING'
                            } as any}
                          />
                        </div>
                      </div>
                    ) : message.type === "upload-status" ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm sm:text-base font-medium text-gray-700 truncate">
                            {message.completed ? "Upload complete" : "Uploading file"}
                            {message.fileName ? (
                              <span className="font-normal text-gray-500">{` · ${message.fileName}`}</span>
                            ) : null}
                          </div>
                          <div
                            className={`flex items-center gap-1 text-xs sm:text-sm ${
                              message.error
                                ? "text-red-600"
                                : message.completed
                                  ? "text-green-600"
                                  : "text-gray-500"
                            }`}
                          >
                            {message.completed ? (
                              <>
                                <CheckCircleIcon className="h-4 w-4" />
                                <span>Done</span>
                              </>
                            ) : (
                              typeof message.progress === "number" && !Number.isNaN(message.progress)
                                ? <span>{`${message.progress}%`}</span>
                                : <span>Processing</span>
                            )}
                          </div>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${
                              message.error
                                ? "bg-red-500"
                                : message.completed
                                  ? "bg-green-500"
                                  : "bg-blue-500"
                            }`}
                            style={{ width: `${Math.min(Math.max((message.progress as number) ?? 0, 0), 100)}%` }}
                          />
                        </div>
                        {message.status && (
                          <p
                            className={`text-xs sm:text-sm ${
                              message.error ? "text-red-600" : "text-gray-500"
                            }`}
                          >
                            {message.status as React.ReactNode}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <div className="text-sm sm:text-base leading-relaxed break-words text-cvc-text">
                          {/* Phase 7: Use new BotMessageContent component with useStreamingMessage hook */}
                          <BotMessageContent
                            message={message}
                            allFacts={allFacts}
                            allDocuments={allDocuments}
                            renderBotMessageWithFacts={renderBotMessageWithFacts}
                          />
                        </div>
                        {/* Inline results (deals + research docs) — rendered per-turn when feature flag is on */}
                        <InlineResultsTabs turnId={message.turnId} />
                        {/* Fact footer - shows source documents referenced in this message */}
                        <ChatFactFooter
                          message={message}
                          onDocumentClick={handleDocumentClick}
                          allFacts={allFacts as Array<{ factId: string; documentId?: string }>}
                          allDocuments={allDocuments as Array<{ documentId: string; title?: string; documentName?: string; originalFilename?: string }>}
                        />
                        {/* Suggested action buttons (Preview/Download from doc-drafter) */}
                        {message.suggested_actions && Array.isArray(message.suggested_actions) && message.suggested_actions.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {message.suggested_actions.map((action, actionIndex) => (
                              <SuggestedActionButton key={actionIndex} action={action} />
                            ))}
                          </div>
                        )}
                        {/* Feedback controls for bot messages only - show only for latest message with turnId */}
                        {index === latestBotMessageIndex && message.turnId && (
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-xs text-cvc-text/70">Help me get better:</span>
                            <button
                              onClick={() => handleFeedback(index, 'thumbs_up')}
                              className={`p-1 rounded-full transition-all duration-200 transform ${
                                scalingMessages.has(index) && messageFeedback[index] === 'thumbs_up'
                                  ? 'scale-125'
                                  : messageFeedback[index] === 'thumbs_up'
                                    ? 'hover:scale-105'
                                    : 'hover:bg-gray-100 hover:scale-105'
                              }`}
                              title="Good response"
                              aria-label="Good response"
                            >
                              {messageFeedback[index] === 'thumbs_up' ? (
                                <HandThumbUpIconSolid className="h-4 w-4 text-cvc-green transition-colors duration-200" />
                              ) : (
                                <HandThumbUpIcon className="h-4 w-4 text-cvc-text/50 hover:text-cvc-green transition-colors duration-200" />
                              )}
                            </button>
                            <button
                              onClick={() => handleFeedback(index, 'thumbs_down')}
                              className={`p-1 rounded-full transition-all duration-200 transform ${
                                scalingMessages.has(index) && messageFeedback[index] === 'thumbs_down'
                                  ? 'scale-125'
                                  : messageFeedback[index] === 'thumbs_down'
                                    ? 'hover:scale-105'
                                    : 'hover:bg-gray-100 hover:scale-105'
                              }`}
                              title="Bad response"
                              aria-label="Bad response"
                            >
                              {messageFeedback[index] === 'thumbs_down' ? (
                                <HandThumbDownIconSolid className="h-4 w-4 text-cvc-coral transition-colors duration-200" />
                              ) : (
                                <HandThumbDownIcon className="h-4 w-4 text-cvc-text/50 hover:text-cvc-coral transition-colors duration-200" />
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Show conversation starters after first message (index 0) */}
              {index === 0 && showConversationStarters && (
                <ConversationStarters
                  onSelectStarter={handleSelectStarter}
                  disabled={pillsDisabled}
                />
              )}
            </React.Fragment>
          );
        })}

        {/* Text feedback box for thumbs down - spans full chat window width, only for latest message with turnId */}
        {latestBotMessageIndex >= 0 && showTextFeedback[latestBotMessageIndex] && chatMessages[latestBotMessageIndex]?.turnId && (
          <div className="w-full mt-2 px-3 sm:px-4 py-3 bg-gray-50 border-t border-gray-200">
            <label className="block text-xs text-gray-600 mb-2">
              What could be improved?
            </label>
            <textarea
              value={textFeedback[latestBotMessageIndex] || ""}
              onChange={(e) => setTextFeedback(prev => ({
                ...prev,
                [latestBotMessageIndex]: e.target.value
              }))}
              placeholder="Share your thoughts..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
              rows={3}
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => handleTextFeedbackSubmit(latestBotMessageIndex)}
                className="px-3 py-1 text-xs bg-cvc-blue text-white rounded hover:bg-cvc-blue/90 transition-colors duration-200"
              >
                Submit
              </button>
              <button
                onClick={() => setShowTextFeedback(prev => ({...prev, [latestBotMessageIndex]: false}))}
                className="px-3 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors duration-200"
              >
                Skip
              </button>
            </div>
          </div>
        )}

        {/* Workflow status indicator - show thinking action with label */}
        {/* Hide once the chat response starts streaming */}
        {/* Use turnId from workflow status itself to avoid stale currentTurnId in workspace mode */}
        {/* Also hide if label is empty (can happen with out-of-order status updates) */}
        {currentWorkflowStatus &&
          currentWorkflowStatus.visible &&
          currentWorkflowStatus.label &&
          !chatMessages.some((msg) => msg.turnId === currentWorkflowStatus.turnId && msg.sender === "chatbot") && (
          <div className="flex justify-start">
            <div className="w-full">
              <div className="flex items-center min-h-[2.5rem]">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-1">
                    <span className="w-2 h-2 bg-cvc-blue rounded-full animate-bounce [animation-delay:0ms]"></span>
                    <span className="w-2 h-2 bg-cvc-blue rounded-full animate-bounce [animation-delay:150ms]"></span>
                    <span className="w-2 h-2 bg-cvc-blue rounded-full animate-bounce [animation-delay:300ms]"></span>
                  </div>
                  <span className="text-sm text-cvc-text animate-[fadeInType_0.5s_ease-in] leading-none flex items-center -translate-y-0.5">
                    {currentWorkflowStatus.label}
                  </span>
                </div>
              </div>

            </div>
          </div>
        )}

        {/* Turn TODO checklist - render BELOW workflow status during active workflow (live updates) */}
        {/* This shows while the workflow is executing, before the response message exists */}
        {currentWorkflowStatus &&
          currentWorkflowStatus.visible &&
          currentTurnTodoPlan &&
          (currentTurnTodoPlan as { steps?: any[] })?.steps &&
          (currentTurnTodoPlan as { steps?: any[] }).steps.length > 0 &&
          // Hide the plan once the chat response starts streaming (when a message exists for this turn)
          // Use turnId from workflow status itself to avoid stale currentTurnId in workspace mode
          !chatMessages.some((msg) => msg.turnId === currentWorkflowStatus.turnId && msg.sender === "chatbot") && (
            <div className="flex justify-start mt-3 mb-2">
              <div className="bg-white rounded-lg shadow-md max-w-md border border-gray-200">
                {/* Card header */}
                <div className="px-4 py-2 bg-cvc-blue rounded-t-lg">
                  <div className="flex items-center space-x-2">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                    <span className="text-sm font-semibold text-white">
                      Plan
                    </span>
                  </div>
                </div>

                {/* Card body - checklist */}
                <div className="px-4 py-3 space-y-2">
                  {(currentTurnTodoPlan as { steps: any[] }).steps.map((step, stepIndex) => {
                    const isComplete = step.status === "complete";
                    const isInProgress = step.status === "in_progress";

                    return (
                      <div
                        key={step.index || stepIndex}
                        className="flex items-start space-x-2.5"
                      >
                        {isComplete ? (
                          <CheckCircleIcon className="w-4 h-4 text-cvc-green mt-0.5 flex-shrink-0" />
                        ) : isInProgress ? (
                          <div className="mt-0.5 flex-shrink-0">
                            <svg
                              className="w-4 h-4 text-cvc-blue animate-spin"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                          </div>
                        ) : (
                          <div className="w-4 h-4 mt-0.5 flex-shrink-0 rounded-full border-2 border-cvc-grey"></div>
                        )}
                        <span
                          className={`text-sm leading-relaxed ${
                            isInProgress
                              ? "text-cvc-blue font-medium"
                              : isComplete
                              ? "text-cvc-text/70"
                              : "text-cvc-text"
                          }`}
                        >
                          {step.friendlyName}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

        {/* Generic typing indicator - show dots only when bot is working but no specific workflow status */}
        {isBotTyping && !currentWorkflowStatus && (
          <div className="flex justify-start">
            <div className="w-full flex items-center min-h-[2.5rem]">
              <div className="flex items-center space-x-1">
                <span className="w-2 h-2 bg-cvc-blue rounded-full animate-bounce [animation-delay:0ms]"></span>
                <span className="w-2 h-2 bg-cvc-blue rounded-full animate-bounce [animation-delay:150ms]"></span>
                <span className="w-2 h-2 bg-cvc-blue rounded-full animate-bounce [animation-delay:300ms]"></span>
              </div>
            </div>
          </div>
        )}

        {/* Empty state (only if no messages) */}
        {chatMessages.length === 0 && (
          <div className="flex justify-center items-center h-32">
            <p className="text-cvc-text/70 text-sm">
              No messages yet. Start a conversation about deals!
            </p>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Feature-specific limitations info bubble */}
      {useCaseSelected && !limitationsDismissed && userMessageCount === 0 && selectedUseCase && USE_CASE_LIMITATIONS[selectedUseCase] && (
        <div className="hidden sm:block px-3 sm:px-4 pb-2">
          <div
            className="relative border border-solid rounded-lg px-4 py-3 pr-10"
            style={{
              borderColor: '#E5E7EB',
              boxShadow: '0 0 20px rgba(0, 0, 0, 0.20)',
              color: '#343434',
            }}
          >
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mb-2">
              BETA
            </span>
            <p className="text-sm leading-relaxed">{USE_CASE_LIMITATIONS[selectedUseCase]}</p>
            <button
              onClick={() => setLimitationsDismissed(true)}
              className="absolute top-3 right-3 p-0.5 rounded hover:bg-gray-100 transition-colors duration-200"
              aria-label="Dismiss limitations info"
            >
              <XMarkIcon className="h-4 w-4" style={{ color: '#234E81' }} />
            </button>
          </div>
        </div>
      )}

      {/* Tablet keyboard spacer - compresses dead space when virtual keyboard is open */}
      {isTablet && showConversationStarters && (
        <div
          className="flex-shrink transition-all duration-200"
          style={{ minHeight: isKeyboardOpen ? '30px' : undefined }}
        />
      )}

      {/* Input */}
      <div>
        <ChatInput
          ariaLabel={chatInputAriaLabel}
          value={inputValue}
          onChange={setInputValue}
        />
      </div>
      </>
      )}
    </div>
  );
}
